/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import { useTooltip, useTooltipInPortal } from "@visx/tooltip";
import { When } from "react-if";
import { useLocation } from "react-router-dom";
import { theme } from "@nordcloud/gnui";
import { Legend } from "~/components/Charts/components";
import {
  ChartType,
  CostAnalysisFields,
  Granularity,
  Period,
  TopGroupsCostChartProps,
} from "~/components/Charts/CostAnalysis/types";
import { BrickLoader } from "~/components/Loaders";
import { ChartDataDisplayWrapper } from "~/components/NoData";
import { LoaderWrap } from "~/components/Utils";
import { EstatePeriod } from "~/constants";
import { ROUTES } from "~/routing/routes";
import { isEmpty } from "~/tools";
import {
  invoiceCorrectionColor,
  tooltipStyles,
  Wrapper,
  StackBarChartData,
} from "./components";
import { getTooltipItemKey } from "./components/utils";
import { getNoDataMessage, isValueLessThanCent } from "./helpers";

type Props = {
  width: number;
  height: number;
  startDate: string;
  endDate: string;
  loading: boolean;
  granularity: Granularity;
  data: TopGroupsCostChartProps[];
  period: EstatePeriod | Period;
  colors: { [k: string]: string };
  keys: string[];
  showOthers?: boolean;
  chartType?: ChartType;
};

export function StackBarChart({
  startDate,
  endDate,
  granularity,
  width,
  height,
  data,
  loading,
  period,
  chartType,
  colors,
  keys,
  showOthers = false,
}: Props) {
  const { pathname } = useLocation();

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip();

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const isEstateView = pathname.includes(ROUTES.estateRecords.indexV2);

  const isCostsBelowCent = data.every(({ value }) =>
    isValueLessThanCent(value)
  );

  const hasNoData = useMemo(
    () =>
      isEmpty(data ?? []) ||
      data?.every((timePoint) => Number(timePoint.value) === 0) ||
      isCostsBelowCent,
    [data, isCostsBelowCent]
  );

  const hasData = !hasNoData;

  return (
    <>
      <Wrapper onMouseLeave={hideTooltip}>
        <LoaderWrap
          loading={loading}
          Component={BrickLoader}
          viewBox="0 0 6 1"
          backgroundColor={theme.color.background.ui01}
        >
          <ChartDataDisplayWrapper
            hasData={hasData}
            messageComponent={getNoDataMessage(
              hasData && isCostsBelowCent,
              granularity
            )}
          >
            <StackBarChartData
              startDate={startDate}
              endDate={endDate}
              granularity={granularity}
              width={width}
              height={height}
              data={data}
              period={period}
              chartType={chartType}
              colors={colors}
              keys={keys}
              showOthers={showOthers}
              showTooltip={showTooltip}
              hideTooltip={hideTooltip}
              containerRef={containerRef}
              isEstateView={isEstateView}
            />
            <Legend
              inline
              data={keys.map((key) => ({
                color: getLegendLabelColor(colors, key),
                label:
                  chartType !== ChartType.ACCUMULATED_COST
                    ? key
                    : getLegendLabel(key, granularity),
              }))}
            />
          </ChartDataDisplayWrapper>
        </LoaderWrap>
        <When condition={tooltipOpen}>
          <TooltipInPortal
            // set this to random so it correctly updates with parent bounds
            key={Date.now()}
            top={tooltipTop}
            left={tooltipLeft}
            // @ts-expect-error need to wait for the library to update it's types
            style={tooltipStyles}
          >
            <>{tooltipData}</>
          </TooltipInPortal>
        </When>
      </Wrapper>
    </>
  );
}

function getLegendLabel(key: string, granularity: Granularity) {
  if (key === CostAnalysisFields.DAILY_COST) {
    return getTooltipItemKey(granularity, key);
  }

  return key;
}

function getLegendLabelColor(
  colors: {
    [k: string]: string;
  },
  key: string
) {
  if (key === CostAnalysisFields.CORRECTION_COST) {
    return invoiceCorrectionColor;
  }

  return colors[key];
}
